import { BorderAll, BugReport, Download } from "@mui/icons-material";
import { Box, Grid, IconButton, LinearProgress, Pagination, Paper, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { getConfigurationLogs } from "src/app/services";
import { ConfigurationLog } from "src/app/types";
import { downloadFile } from "src/app/utils/functions";
import { convertUTCDateToLocalTime, formatToUTC } from "src/app/utils/helper";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#e8e8ed",
        color: theme.palette.common.black,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const ConfigurationLogsPageContent = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [logs, setLogs] = useState<ConfigurationLog[]>([]);
    const [page, setPage] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);

    useEffect(() => {
        setLoading(true);
        getConfigurationLogs(page)
            .then((response) => {
                setLogs(response.data.rows);
                setTotalLogs(response.data.total);
            })
            .catch((err) => {
                enqueueSnackbar({
                    message: "Error fetching configuration logs.",
                    variant: "error",
                });
            })
            .finally(() => setLoading(false));
    }, [page]);

    const getActionName = useCallback((action: number) => {
        switch (action) {
            case 0:
                return "Created";
            case 1:
                return "Deleted";
            case 2:
                return "Updated";
            case 3:
                return "Deactivated";
            case 4:
                return "Activated";
            default:
                return "Unknown";
        }
    }, []);

    const formatDuration = useCallback((duration: string) => {
        if (duration === "Inactive") {
            return duration;
        }

        const seconds = parseInt(duration);

        if (seconds <= 60) {
            return `${seconds} seconds`;
        } else if (seconds <= 60 * 60) {
            return `${Math.floor(seconds / 60)} minutes`;
        } else if (seconds <= 60 * 60 * 24) {
            return `${Math.floor(seconds / 60 / 60)} hours`;
        } else {
            return `${Math.floor(seconds / 60 / 60 / 24)} days`;
        }
    }, []);

    const generateCsv = useCallback((name: string, data: any) => {
        try {
            const parsedData: any = JSON.parse(data);

            const rows: string[][] = [];

            console.log(parsedData);

            rows.push(["[CONFIGURATION NAME]", parsedData.configuration.name]);
            rows.push(["[Client name]", parsedData.configuration.client_name]);
            rows.push(["[Sampling Rate]", parsedData.configuration.sampling_rate]);
            rows.push([""]);
            rows.push([""]);

            parsedData.modules.forEach((module: any) => {
                rows.push(["[MODULE NAME]", module.name]);

                const fromData = JSON.parse(module.from_data);
                for (const key in fromData) {
                    if (key !== "configuration_id" && key !== "module_id") {
                        if (key === "overWrite") {
                            for (const overwriteKey in fromData[key]) {
                                rows.push(["", overwriteKey, fromData[key][overwriteKey]]);
                            }
                        } else {
                            rows.push(["", key, `"${fromData[key]}"`]);
                        }
                    }
                }

                rows.push([""]);
            });

            const csvContent = rows.map((e) => e.join(",")).join("\n");

            downloadFile(
                `${name ? name : "Deleted Configuration"}.csv`,
                csvContent,
                "text/csv"
            );
        } catch (err) {
            enqueueSnackbar({
                message: "Error parsing JSON.",
                variant: "error",
            });
        }
    }, []);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ my: loading ? 1 : 2 }}>{loading && <LinearProgress />}</Box>
            <Grid container>
                <Grid item xs={12} lg={12}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 500 }} aria-label="customized table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell align="center">Logged At</StyledTableCell>
                                    <StyledTableCell align="center">
                                        Configuration
                                    </StyledTableCell>
                                    <StyledTableCell align="center">User</StyledTableCell>
                                    <StyledTableCell align="center">Action</StyledTableCell>
                                    <StyledTableCell align="center">
                                        Active Duration
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        Deactivated At
                                    </StyledTableCell>
                                    <StyledTableCell align="center">Download</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.length === 0 && (
                                    <StyledTableRow>
                                        <StyledTableCell align="center" colSpan={7}>
                                            {loading ? "Loading..." : "No logs."}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                                {logs.map((row, i) => (
                                    <StyledTableRow key={i}>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {formatToUTC(
                                                new Date(row.createdAt * 1000)
                                            )}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.configurationName
                                                ? row.configurationName
                                                : "Deleted Configuration"}
                                        </StyledTableCell>
                                        {/* //----- */}
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.userName}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {getActionName(row.action)}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {formatDuration(row.activeDuration)}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.deactivatedAt
                                                ? convertUTCDateToLocalTime(
                                                    new Date(row.deactivatedAt * 1000)
                                                )
                                                : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <IconButton
                                                onClick={() => {
                                                    downloadFile(
                                                        `${row.configurationName
                                                            ? row.configurationName
                                                            : "Deleted Configuration"
                                                        }.ini`,
                                                        row.fileData,
                                                        "text/plain"
                                                    );
                                                }}
                                                aria-label="Export ini"
                                                color="primary"
                                                title="Download File"
                                            >
                                                <Download />
                                            </IconButton>
                                            {/* <IconButton
                                                onClick={() => {
                                                    generateCsv(row.configurationName, row.moduleData);
                                                }}
                                                aria-label="Export csv"
                                                color="primary"
                                                title="Download CSV"
                                            >
                                                <BorderAll />
                                            </IconButton> */}
                                            {/* <IconButton
                                                onClick={() => {
                                                    downloadFile(
                                                        `${row.configurationName
                                                            ? row.configurationName
                                                            : "Deleted Configuration"
                                                        }.json`,
                                                        row.moduleData,
                                                        "application/json"
                                                    );
                                                }}
                                                aria-label="Export json"
                                                color="primary"
                                                title="Download JSON"
                                            >
                                                <BugReport />
                                            </IconButton> */}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "20px",
                        }}
                    >
                        <Pagination
                            count={Math.ceil(totalLogs / 10)}
                            shape="rounded"
                            size="large"
                            page={page}
                            onChange={(e, newPage) => setPage(newPage)}
                            color="primary"
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ConfigurationLogsPageContent;